import { SESSION_STORAGE_KEYS } from '@src/constants/sessionStorage';
import { fetchNotifications } from '@src/store/company/companyInAppNotificationSlice';
import { AppDispatch, RootState } from '@src/store/store';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const POLLING_INTERVAL = 60;

export const useCompanyInAppNotificationPolling = (isLogin: boolean) => {
  const dispatch = useDispatch<AppDispatch>();
  const { notifications, latestCreatedAt } = useSelector(
    (state: RootState) => state.companyInAppNotification,
  );
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const previousLatestCreatedAtRef = useRef<string | null>(null);
  const [isExistNewNotification, setIsExistNewNotification] = useState(false);

  useEffect(() => {
    if (!isLogin) {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      return;
    }

    // 初回fetch
    dispatch(fetchNotifications());

    // 60秒ポーリング
    intervalIdRef.current = setInterval(() => {
      dispatch(fetchNotifications());
    }, POLLING_INTERVAL * 1000); // ミリ秒に変換

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [isLogin, dispatch]);

  // latestCreatedAtが更新されたら新着通知をチェック
  useEffect(() => {
    if (!latestCreatedAt) {
      setIsExistNewNotification(false);
      return;
    }

    // 初回はsessionStorageから前回チェック済みの時刻を取得
    if (previousLatestCreatedAtRef.current === null) {
      const lastCheckedAt = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.LAST_CHECKED_NOTIFICATION_AT_COMPANY,
      );
      if (!lastCheckedAt) {
        setIsExistNewNotification(!!latestCreatedAt);
      }
      previousLatestCreatedAtRef.current = lastCheckedAt;
    }

    // 前回チェック済みの時刻がある場合のみ比較
    if (previousLatestCreatedAtRef.current) {
      const previousTime = new Date(previousLatestCreatedAtRef.current).getTime();
      const currentTime = new Date(latestCreatedAt).getTime();
      setIsExistNewNotification(currentTime > previousTime);
    }
    previousLatestCreatedAtRef.current = latestCreatedAt;
  }, [latestCreatedAt]);

  const resetNewNotification = () => {
    setIsExistNewNotification(false);
    if (!latestCreatedAt) {
      return;
    }
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.LAST_CHECKED_NOTIFICATION_AT_COMPANY,
      latestCreatedAt,
    );
  };

  return {
    notifications,
    isExistNewNotification,
    resetNewNotification,
  };
};
